import { EdvisorMessage } from './edvisorService';

export interface SuggestedPrompt {
  id: string;
  text: string;
  category: 'school_search' | 'program_search' | 'eligibility' | 'events' | 'tours' | 'favorites' | 'comparison' | 'location_based';
  toolTrigger: string;
}

export class SuggestedPromptsService {
  
  // Memory management for shown prompts
  private getShownPromptIds(): string[] {
    const stored = sessionStorage.getItem('edvisorShownPrompts');
    return stored ? JSON.parse(stored) : [];
  }

  private addShownPromptId(promptId: string): void {
    const shown = this.getShownPromptIds();
    shown.push(promptId);
    sessionStorage.setItem('edvisorShownPrompts', JSON.stringify(shown));
  }

  private resetShownPrompts(): void {
    sessionStorage.removeItem('edvisorShownPrompts');
  }

  // Public method to reset memory (for thread changes)
  public resetMemory(): void {
    this.resetShownPrompts();
  }

  private getAvailablePrompts(allPrompts: SuggestedPrompt[]): SuggestedPrompt[] {
    const shownPrompts = this.getShownPromptIds();
    
    // Filter out recently shown prompts
    const availablePrompts = allPrompts.filter(prompt => 
      !shownPrompts.includes(prompt.id)
    );
    
    // If fewer than 3 available, reset memory and use all prompts
    if (availablePrompts.length < 3) {
      this.resetShownPrompts();
      return allPrompts;
    }
    
    return availablePrompts;
  }

  generatePrompts(message: EdvisorMessage, conversationHistory: EdvisorMessage[]): SuggestedPrompt[] {
    const prompts: SuggestedPrompt[] = [];
    const content = message.content.toLowerCase();
    
    // Check if this is a welcome message
    if (this.isWelcomeMessage(message.content)) {
      return this.getWelcomePrompts();
    }
    
    // Extract entities from message content
    const extractedEntities = this.extractEntities(message.content);
    
    // Use priority-based selection - only pick ONE category to avoid overlap
    let selectedCategory = '';
    
    // Priority order: School > Program > Events > Eligibility > Sports > Location
    if (this.mentionsSchool(content)) {
      selectedCategory = 'school';
      prompts.push(...this.getSchoolPrompts(extractedEntities.schoolNames));
    } else if (this.mentionsProgram(content)) {
      selectedCategory = 'program';
      prompts.push(...this.getProgramPrompts(extractedEntities.programNames));
    } else if (this.mentionsEvents(content)) {
      selectedCategory = 'events';
      prompts.push(...this.getEventPrompts());
    } else if (this.mentionsEligibility(content)) {
      selectedCategory = 'eligibility';
      prompts.push(...this.getEligibilityPrompts());
    } else if (this.mentionsSports(content)) {
      selectedCategory = 'sports';
      prompts.push(...this.getSportsPrompts());
    } else if (this.mentionsLocation(content)) {
      selectedCategory = 'location';
      prompts.push(...this.getLocationPrompts());
    }
    
    // Only add general prompts if no specific category was selected
    if (!selectedCategory) {
      prompts.push(...this.getGeneralPrompts());
    }
    
    // Remove duplicates first
    const uniquePrompts = this.removeDuplicates(prompts);
    
    // Get available prompts (excluding recently shown ones)
    const availablePrompts = this.getAvailablePrompts(uniquePrompts);
    
    // Select up to 3 prompts from available options
    const selectedPrompts = availablePrompts.slice(0, 3);
    
    // Mark selected prompts as shown
    selectedPrompts.forEach(prompt => {
      this.addShownPromptId(prompt.id);
    });
    
    return selectedPrompts;
  }
  
  // Extract entities from message content
  private extractEntities(content: string): { schoolNames: string[], programNames: string[] } {
    const schoolNames: string[] = [];
    const programNames: string[] = [];
    
    // Extract school names from ![S|School Name|School ID] format
    const schoolRegex = /!\[S\|([^|]+)\|([^\]]+)\]/g;
    let match;
    while ((match = schoolRegex.exec(content)) !== null) {
      schoolNames.push(match[1].trim());
    }
    
    // Extract program names from ![P|Program Name|Program ID] format
    const programRegex = /!\[P\|([^|]+)\|([^\]]+)\]/g;
    while ((match = programRegex.exec(content)) !== null) {
      programNames.push(match[1].trim());
    }
    
    // If no markup found, try to extract from regular text
    if (schoolNames.length === 0) {
      const commonSchoolNames = [
        'University of Toronto', 'McMaster University', 'University of Waterloo', 'York University',
        'Ryerson University', 'University of British Columbia', 'Simon Fraser University',
        'University of Alberta', 'University of Calgary', 'University of Ottawa',
        'Carleton University', 'Queen\'s University', 'Western University',
        'University of Guelph', 'Wilfrid Laurier University', 'University of Windsor',
        'Brock University', 'Trent University', 'Lakehead University',
        'Laurentian University', 'Nipissing University', 'University of Sudbury'
      ];
      
      for (const schoolName of commonSchoolNames) {
        if (content.includes(schoolName)) {
          schoolNames.push(schoolName);
          break; // Only take the first match
        }
      }
    }
    
    return { schoolNames, programNames };
  }
  
  // Check if message contains old [[button]] markup (for debugging)
  private hasOldButtonMarkup(content: string): boolean {
    const buttonRegex = /\[\[([^\]]+)\]\]/g;
    return buttonRegex.test(content);
  }

  // School-related prompts (triggers: get_school_by_name, get_programs_by_school, etc.)
  private getSchoolPrompts(schoolNames: string[]): SuggestedPrompt[] {
    const schoolName = schoolNames.length > 0 ? schoolNames[0] : 'this school';
    
    return [
      { 
        id: 'school-events', 
        text: `Show me upcoming events at ${schoolName}`, 
        category: 'events',
        toolTrigger: 'get_events_by_school'
      },
      { 
        id: 'school-tour', 
        text: `Book a campus visit or virtual tour at ${schoolName}`, 
        category: 'tours',
        toolTrigger: 'handle_tour_action'
      },
      { 
        id: 'add-school', 
        text: `Add ${schoolName} to my favorites`, 
        category: 'favorites',
        toolTrigger: 'add_school_to_favorites'
      }
    ];
  }
  
  // Program-related prompts (triggers: get_program_by_name, get_program_by_location)
  private getProgramPrompts(programNames: string[]): SuggestedPrompt[] {
    const programName = programNames.length > 0 ? programNames[0] : 'this program';
    
    return [
      { 
        id: 'check-eligibility', 
        text: `Am I eligible for ${programName}?`, 
        category: 'eligibility',
        toolTrigger: 'get_program_by_name'
      },
      { 
        id: 'similar-programs', 
        text: `Show me similar programs to ${programName}`, 
        category: 'program_search',
        toolTrigger: 'get_schools_by_program_category'
      },
      { 
        id: 'program-locations', 
        text: `Where else is ${programName} offered?`, 
        category: 'location_based',
        toolTrigger: 'get_program_by_location'
      },
      { 
        id: 'add-program', 
        text: `Add ${programName} to my favorites`, 
        category: 'favorites',
        toolTrigger: 'add_program_to_favorites'
      },
      { 
        id: 'compare-programs', 
        text: `Compare the ${programName} program to similar programs`, 
        category: 'comparison',
        toolTrigger: 'compare_programs'
      }
    ];
  }
  
  // Location-based prompts (triggers: get_school_by_location, get_program_by_location, get_schools_by_province)
  private getLocationPrompts(): SuggestedPrompt[] {
    return [
      { 
        id: 'schools-nearby', 
        text: 'Find schools in this area', 
        category: 'location_based',
        toolTrigger: 'get_school_by_location'
      },
      { 
        id: 'programs-nearby', 
        text: 'Find programs in this area', 
        category: 'location_based',
        toolTrigger: 'get_program_by_location'
      },
      { 
        id: 'province-schools', 
        text: 'Show me schools in this province', 
        category: 'location_based',
        toolTrigger: 'get_schools_by_province'
      }
    ];
  }
  
  // Sports-related prompts (triggers: get_school_by_sport)
  private getSportsPrompts(): SuggestedPrompt[] {
    return [
      { 
        id: 'schools-sport', 
        text: 'Which schools offer this sport?', 
        category: 'school_search',
        toolTrigger: 'get_school_by_sport'
      },
      { 
        id: 'other-sports', 
        text: 'What other sports are available?', 
        category: 'school_search',
        toolTrigger: 'get_school_by_sport'
      }
    ];
  }
  
  // Event-related prompts (triggers: get_events_by_school, get_events_by_category)
  private getEventPrompts(): SuggestedPrompt[] {
    return [
      { 
        id: 'more-events', 
        text: 'Show me more events like this', 
        category: 'events',
        toolTrigger: 'get_events_by_category'
      },
      { 
        id: 'school-events', 
        text: 'Find events at this school', 
        category: 'events',
        toolTrigger: 'get_events_by_school'
      },
      { 
        id: 'category-events', 
        text: 'Show me all admission events', 
        category: 'events',
        toolTrigger: 'get_events_by_category'
      }
    ];
  }
  
  // Eligibility prompts (triggers: get_program_by_name with eligibility check)
  private getEligibilityPrompts(): SuggestedPrompt[] {
    return [
      { 
        id: 'improve-eligibility', 
        text: 'How can I improve my eligibility?', 
        category: 'eligibility',
        toolTrigger: 'get_program_by_name'
      },
      { 
        id: 'eligible-programs', 
        text: 'What programs am I eligible for?', 
        category: 'eligibility',
        toolTrigger: 'get_program_by_name'
      }
    ];
  }
  
  // General prompts (always available)
  private isWelcomeMessage(content: string): boolean {
    const welcomePhrases = [
      "i'm ed visor",
      "educational guidance counselor",
      "how can i assist you today",
      "navigate canadian post-secondary education"
    ];
    
    const lowerContent = content.toLowerCase();
    return welcomePhrases.some(phrase => lowerContent.includes(phrase));
  }
  
  private getWelcomePrompts(): SuggestedPrompt[] {
    return [
      { 
        id: 'uot-programs', 
        text: 'Show me programs offered by U of T', 
        category: 'program_search',
        toolTrigger: 'get_programs_by_school'
      },
      { 
        id: 'toronto-met-info', 
        text: 'Tell me about Toronto Metropolitan University', 
        category: 'school_search',
        toolTrigger: 'get_school_by_name'
      },
      { 
        id: 'waterloo-events', 
        text: 'Show me upcoming events at University of Waterloo', 
        category: 'events',
        toolTrigger: 'get_events_by_school'
      }
    ];
  }

  private getGeneralPrompts(): SuggestedPrompt[] {
    return [
      { 
        id: 'my-favorites', 
        text: 'Show me my favorites', 
        category: 'favorites',
        toolTrigger: 'get_user_favorites'
      },
      { 
        id: 'compare', 
        text: 'Compare schools', 
        category: 'comparison',
        toolTrigger: 'compare_schools'
      },
      { 
        id: 'near-me', 
        text: 'Find schools near me', 
        category: 'location_based',
        toolTrigger: 'get_school_by_location'
      }
    ];
  }
  
  // Detection methods
  private mentionsSchool(content: string): boolean {
    return /school|university|college|campus/.test(content);
  }
  
  private mentionsProgram(content: string): boolean {
    return /program|degree|bachelor|master|course/.test(content);
  }
  
  private mentionsLocation(content: string): boolean {
    return /toronto|ontario|vancouver|montreal|near|close|area/.test(content);
  }
  
  private mentionsSports(content: string): boolean {
    return /sport|athletic|basketball|hockey|soccer|volleyball/.test(content);
  }
  
  private mentionsEvents(content: string): boolean {
    return /event|open house|tour|visit|session/.test(content);
  }
  
  private mentionsEligibility(content: string): boolean {
    return /eligible|eligibility|qualify|requirement|admit/.test(content);
  }
  
  private removeDuplicates(prompts: SuggestedPrompt[]): SuggestedPrompt[] {
    const seen = new Set();
    const result: SuggestedPrompt[] = [];
    
    for (const prompt of prompts) {
      // Check for exact ID duplicates
      if (seen.has(prompt.id)) continue;
      
      // Check for similar text content (avoid redundant prompts)
      const isSimilar = result.some(existing => 
        this.arePromptsSimilar(prompt.text, existing.text)
      );
      
      if (!isSimilar) {
        seen.add(prompt.id);
        result.push(prompt);
      }
    }
    
    return result;
  }
  
  private arePromptsSimilar(text1: string, text2: string): boolean {
    // Normalize text for comparison
    const normalize = (text: string) => text.toLowerCase()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    
    const norm1 = normalize(text1);
    const norm2 = normalize(text2);
    
    // Check if one contains the other (for cases like "What programs does X offer?" vs "Show me programs at X")
    if (norm1.includes(norm2) || norm2.includes(norm1)) {
      return true;
    }
    
    // Check for very similar wording
    const words1 = norm1.split(' ');
    const words2 = norm2.split(' ');
    const commonWords = words1.filter(word => words2.includes(word));
    
    // If more than 70% of words are common, consider them similar
    return commonWords.length / Math.max(words1.length, words2.length) > 0.7;
  }
}
